import React from "react";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopRingtone } from "../../util/ringtone";
import { resolveCallExitPath } from "../../util/afterCallNavigate";

function readLiveCall() {
  try {
    const appt = JSON.parse(localStorage.getItem("appointmentData") || "{}");
    const profile = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const appointmentId = String(appt?.appointmentId || "");
    const hadSession = !!(
      appointmentId ||
      appt?.sessionStartTime ||
      appt?.sessionId ||
      localStorage.getItem("joined") === "true"
    );
    let role = profile?.roleCode || "";
    if (!role) {
      const myId = String(profile?._id || "");
      const clientId = String(appt?.clientId || appt?.callerData?.user?.id || "");
      if (myId && clientId && myId === clientId) role = "user";
    }
    return { appointmentId, hadSession, role };
  } catch (_) {
    return { appointmentId: "", hadSession: false, role: "" };
  }
}

/**
 * A render crash during OpenTok reconnect must never end a live consult.
 * getDerivedStateFromError runs before componentDidCatch — hadSession has to
 * live in React state or the first paint always said "This consultation has ended".
 */
class CallErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, hadSession: false, appointmentId: "", role: "" };
  }

  static getDerivedStateFromError() {
    const live = readLiveCall();
    return { hasError: true, ...live };
  }

  componentDidCatch(error, info) {
    console.error("Call room crashed:", error, info);
    try {
      stopRingtone("disconnect");
    } catch (_) {
      /* ignore */
    }
    // Do not appointmentClose. Do not tear down media here — the user can
    // reload to rejoin. Closing here left the peer on "Reconnecting media…".
  }

  handleReturn = () => {
    try {
      stopAllCallMedia();
    } catch (_) {
      /* ignore */
    }
    try {
      stopAllRegisteredMedia();
    } catch (_) {
      /* ignore */
    }
    try {
      window.location.replace(
        resolveCallExitPath({
          appointmentId: this.state.appointmentId,
          roleCode: this.state.role,
        })
      );
    } catch (_) {
      window.location.replace("/appointments");
    }
  };

  handleRejoin = () => {
    try {
      window.location.reload();
    } catch (_) {
      this.handleReturn();
    }
  };

  render() {
    if (this.state.hasError) {
      const live = this.state.hadSession;
      return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950 p-4 text-white">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-7 text-center shadow-2xl">
            <h2 className="text-xl font-bold">
              {live ? "Connection problem" : "This consultation has ended"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-white/75">
              {live
                ? "The call screen hit a connection problem. The consultation is still open — rejoin to restore camera and microphone."
                : "The call screen closed. Your camera and microphone have been released."}
            </p>
            {live ? (
              <button
                type="button"
                onClick={this.handleRejoin}
                className="mt-5 w-full rounded-lg bg-prontopsy-blue px-4 py-2 text-sm font-bold text-white">
                Rejoin call
              </button>
            ) : null}
            <button
              type="button"
              onClick={this.handleReturn}
              className={
                live
                  ? "mt-3 w-full rounded-lg border border-white/20 px-4 py-2 text-sm font-semibold text-white/90"
                  : "mt-5 w-full rounded-lg bg-prontopsy-blue px-4 py-2 text-sm font-bold text-white"
              }>
              Return to my appointments
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CallErrorBoundary;
